import { computed, inject, Injectable, signal } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import Keycloak from 'keycloak-js';
import { uiLabel } from 'hx-services';
import { CatalogMeService } from '@adm-web-app/modules/composite-products/catalog/catalog-me.service';
import { MEDIA_API_BASE, MediaCopy, MediaCrop, MediaItem, MediaKind, MediaLimits, MediaList } from './media-api';
import { FileFacts, planBatch, RejectReason } from './media-checks';
import { isForbidden, MEDIA_TEXTS, mediaErrorOf, mediaErrorText } from './media-errors';
import { inspectFile } from './media-file-probe';
import { formatClock, formatDimensions, formatSize, pageLang } from './media-format';
import { ProductMediaHttp } from './media-http.service';
import { mergeOrder, orderNeedsSave, reorderVisible, sameOrder, serverOrder, visibleOrder, wireOrder } from './media-order';
import { MediaPreviewCache } from './media-preview-cache';
import { UploadUrlRejectedError } from './media-resume';
import { buildTiles, countTiles, publicThumb, Tile, versioned } from './media-tiles';
import { MediaUploader, UploadHooks } from './media-uploader';
import { UndoQueue, UNDO_TTL_MS } from './media-undo-queue';
import { Sniffed } from './media-sniff';

/** The limits of the contract, used until the server tells its own (and when it cannot). */
export const DEFAULT_LIMITS: MediaLimits = {
  photos: 5,
  videos: 1,
  videoSeconds: 30,
  photoBytes: 25 * 1024 * 1024,
  videoBytes: 300 * 1024 * 1024,
  photoMinSide: 600,
  photoGoodSide: 1200,
  videoMinSide: 720
};

export interface MediaMessage {
  id: number;
  level: 'error' | 'warn' | 'info';
  /** The name of a file the line is about (drawn in bold, before the text). */
  file: string | null;
  text: string;
  /** Messages of the same kind replace each other instead of piling up. */
  group: string;
}

/** A deletion that can still be taken back. */
export interface DeleteToast {
  id: number;
  kind: MediaKind;
}

export type SavedNote = 'order' | 'deleted' | 'uploaded' | 'frame' | 'copied' | 'retry' | 'replaced';

/** Another size of the cake, for the list «Взять из другого размера». */
export interface Sibling {
  id: number;
  title: string;
  photos: number;
  videos: number;
  ready: number;
  thumb: { src: string; srcset: string | null } | null;
}

const POLL_FAST_MS = 3000;
const POLL_SLOW_MS = 5000;
/**
 * The old photo comes back when the last photo is deleted, but the server puts the file itself back a little later (the writer of the old pictures runs every 15 s), and the
 * browser keeps a picture for an hour: the old photo is asked for again twice after it appears, once the file has certainly been put back.
 */
const LEGACY_RESTAMP_MS: readonly number[] = [20_000, 45_000];

/**
 * Everything the tab «Фото и видео» of a size does, as signals: the gallery from the server, the order the editor is putting it in, the deletions that wait their ten
 * seconds, the files on their way, and the notices. One per tab (provided by the component). Every change is saved at once; the buttons that start a request are
 * out of reach until its answer comes (`busy`, `orderBusy`).
 */
@Injectable()
export class ProductMediaState {
  readonly list = signal<MediaList | null>(null);
  readonly loading = signal(true);
  readonly loadFailed = signal<unknown>(null);
  readonly messages = signal<MediaMessage[]>([]);
  readonly savedNote = signal<SavedNote | null>(null);
  /** The order of the ids the editor has put things in, until the server has it. */
  readonly desired = signal<number[] | null>(null);
  readonly orderBusy = signal(false);
  /** The items with a request of their own on its way (retry, frame, delete). */
  readonly busy = signal<ReadonlySet<number>>(new Set());
  readonly copyBusy = signal(false);
  readonly siblings = signal<Sibling[] | null>(null);
  readonly siblingsLoading = signal(false);
  readonly siblingsFailed = signal(false);
  /** The user may write the cards (the same right as `canCreateCards` of the catalog: the admin and the holders of `composite_product_edit`). */
  readonly canWrite = signal(false);
  private readonly forbidden = signal(false);

  readonly uploader = inject(MediaUploader);
  readonly previews = inject(MediaPreviewCache);
  private readonly api = inject(ProductMediaHttp);
  private readonly translate = inject(TranslocoService);
  private readonly catalogMe = inject(CatalogMeService);
  private readonly keycloak = inject(Keycloak, { optional: true });

  private readonly queued = signal<number[]>([]);
  private readonly deleting = signal<ReadonlySet<number>>(new Set());
  private readonly queue = new UndoQueue<DeleteToast>(
    UNDO_TTL_MS,
    (id, toast) => void this.commitDelete(id, toast),
    () => this.queued.set(this.queue.keys())
  );
  private readonly localThumbs = signal<ReadonlyMap<number, string>>(new Map());
  /** When the old photo was last read anew (it appeared, or its address changed): the address carries it, so the browser's hour-old copy is not shown. */
  private readonly legacyStamp = signal(0);
  private readonly commits = new Set<Promise<void>>();
  /** The deletions the page sent as it was being closed (`pagehide`): a page restored from the back/forward cache must not offer them again. */
  private readonly sentOnUnload = new Set<number>();
  /** The uploads that wait for the network (by the number of their hooks). */
  private readonly offline = new Set<number>();
  private hookSeq = 0;
  private productInfoId = 0;
  private requestSeq = 0;
  private messageSeq = 0;
  private pollTimer: ReturnType<typeof setTimeout> | null = null;
  private restampTimers: ReturnType<typeof setTimeout>[] = [];
  private destroyed = false;

  /** The ids the editor does not see: the deletions that wait, and the ones being sent. */
  readonly hidden = computed<ReadonlySet<number>>(() => new Set([...this.queued(), ...this.deleting()]));
  readonly limits = computed(() => this.list()?.limits ?? DEFAULT_LIMITS);
  readonly fullOrder = computed(() => mergeOrder(this.desired(), serverOrder(this.list()?.items ?? [])));
  readonly shownOrder = computed(() => visibleOrder(this.fullOrder(), this.hidden()));
  readonly tiles = computed<Tile[]>(() =>
    buildTiles({ items: this.list()?.items ?? [], order: this.shownOrder(), hidden: this.hidden(), uploads: this.uploader.uploads(), thumbs: this.localThumbs() })
  );
  readonly counts = computed(() => countTiles(this.tiles()));
  /** The deletions that can still be taken back, for the toasts. */
  readonly toasts = computed<DeleteToast[]>(() => this.queued().map(id => this.queue.get(id)).filter((t): t is DeleteToast => !!t));
  /** The feature is off on this server, or the user may not write: the gallery is shown, not changed. */
  readonly readOnly = computed<'disabled' | 'rights' | null>(() => {
    if (this.list()?.enabled === false) {
      return 'disabled';
    }
    return this.canWrite() && !this.forbidden() ? null : 'rights';
  });
  readonly editable = computed(() => this.readOnly() === null && this.list() !== null);
  /** The old photo of the size (read-only tile and card), with the time it was read in the address: the file is cached by the browser for an hour and is rewritten by the server. */
  readonly legacyUrl = computed(() => {
    const url = this.list()?.legacyPhoto?.url;
    return url ? versioned(url, this.legacyStamp()) : null;
  });
  /** The text of why the gallery could not be read. */
  readonly loadFailedText = computed(() => {
    const failure = this.loadFailed();
    if (!failure) {
      return '';
    }
    const error = mediaErrorOf(failure);
    return error ? mediaErrorText(this.text, error.message, error.data, this.lang()) : this.t('errors.failed');
  });

  async init(productInfoId: number): Promise<void> {
    this.productInfoId = productInfoId;
    const rights = this.catalogMe.me().then(me => this.canWrite.set(me.canCreateCards), () => this.canWrite.set(false));
    this.loading.set(true);
    try {
      await Promise.all([rights, this.refresh()]);
    } finally {
      this.loading.set(false);
    }
  }

  /** Reads the gallery again. On the first read a failure is shown; on the later ones (the poll) it is quietly tried again. */
  async refresh(): Promise<void> {
    if (this.destroyed) {
      return;
    }
    const seq = ++this.requestSeq;
    try {
      const list = await this.api.list(this.productInfoId);
      if (seq === this.requestSeq && !this.destroyed) {
        this.apply(list);
        this.loadFailed.set(null);
      } else {
        // an answer of something newer is (or was) applied; the poll must not end with this one
        this.schedulePoll();
      }
    } catch (e) {
      if (this.list() === null) {
        this.loadFailed.set(e);
      } else {
        this.schedulePoll();
      }
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------ order

  moveVisible(from: number, to: number): void {
    if (!this.editable() || this.orderBusy()) {
      return;
    }
    const next = reorderVisible(this.fullOrder(), this.hidden(), from, to);
    if (sameOrder(next, this.fullOrder())) {
      return;
    }
    this.desired.set(next);
    void this.syncOrder();
  }

  step(id: number, direction: -1 | 1): void {
    const at = this.shownOrder().indexOf(id);
    if (at >= 0) {
      this.moveVisible(at, at + direction);
    }
  }

  makeMain(id: number): void {
    const at = this.shownOrder().indexOf(id);
    if (at > 0) {
      this.moveVisible(at, 0);
    }
  }

  /**
   * Tells the server the order, when it is not the one it has. The hidden items (not deleted yet) go last: the server wants exactly its live items. The ones whose `DELETE`
   * is on its way are left out. If the server says the set of items is not the one sent (`media.order.mismatch`: a deletion or an upload that this page did not know yet
   * has landed), the list is read again and the order is sent once more; only a second refusal is told.
   */
  async syncOrder(retried = false): Promise<void> {
    const list = this.list();
    if (!list || this.orderBusy() || !this.editable()) {
      return;
    }
    const full = this.fullOrder();
    const hidden = this.hidden();
    const gone = this.deleting();
    if (!orderNeedsSave(full, hidden, serverOrder(list.items), gone)) {
      this.settleDesired();
      return;
    }
    this.orderBusy.set(true);
    let again = false;
    try {
      const answer = await this.api.reorder({ productInfoId: this.productInfoId, ids: wireOrder(full, hidden, gone) });
      this.invalidateReads();
      this.apply(answer);
      this.savedNote.set('order');
    } catch (e) {
      again = !retried && mediaErrorOf(e)?.message === 'media.order.mismatch';
      if (!again) {
        this.desired.set(null);
        this.fail(e, 'order');
      }
      await this.refresh();
    } finally {
      this.orderBusy.set(false);
    }
    if (again) {
      await this.syncOrder(true);
      return;
    }
    this.settleDesired();
    // the editor may have changed things while the answer was on its way (a deletion taken back)
    const next = this.list();
    if (next && orderNeedsSave(this.fullOrder(), this.hidden(), serverOrder(next.items), this.deleting()) && this.desired() !== null) {
      await this.syncOrder();
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------ delete and undo

  /** Hides an item and starts its ten seconds; the `DELETE` goes when they are over. */
  remove(id: number): void {
    const tile = this.tiles().find(t => t.id === id);
    if (!tile || !this.editable() || this.queue.has(id)) {
      return;
    }
    // a file that stopped on its way is forgotten with its item (its tile must not come back when the item is gone)
    const stopped = this.uploader.uploads().find(u => u.itemId === id);
    if (stopped) {
      this.uploader.discard(stopped.uid);
    }
    this.queue.add(id, { id, kind: tile.kind });
    this.savedNote.set('deleted');
  }

  undo(id: number): void {
    if (this.queue.undo(id)) {
      this.savedNote.set(null);
      void this.syncOrder();
    }
  }

  /** Sends every deletion that waits, now, and waits for the answers: the editor leaves, or starts something that must meet the real state of the size. */
  async flushDeletes(): Promise<void> {
    this.queue.flush();
    while (this.commits.size > 0) {
      await Promise.all([...this.commits]);
    }
  }

  /**
   * The browser closes the page: the deletions that wait are sent in a way that outlives it. They leave the queue (no timer, no «Отменить») and stay hidden as «being sent», so a
   * page that comes back from the back/forward cache (`restoredFromCache`) neither offers to take them back nor sends them a second time.
   */
  flushOnUnload(): void {
    const token = this.keycloak?.token;
    const ids = this.queue.keys();
    for (const id of ids) {
      void fetch(`${MEDIA_API_BASE}/${id}`, { method: 'DELETE', keepalive: true, headers: token ? { Authorization: `Bearer ${token}` } : {} }).catch(() => undefined);
    }
    if (ids.length === 0) {
      return;
    }
    this.deleting.update(set => new Set([...set, ...ids]));
    for (const id of ids) {
      this.sentOnUnload.add(id);
    }
    this.queue.dispose();
    this.queued.set([]);
  }

  /** The page is shown again from the back/forward cache: what it sent on its way out is done, the list is read anew. */
  restoredFromCache(): void {
    if (this.sentOnUnload.size > 0) {
      const sent = new Set(this.sentOnUnload);
      this.sentOnUnload.clear();
      this.deleting.update(set => new Set([...set].filter(id => !sent.has(id))));
    }
    void this.refresh();
  }

  private commitDelete(id: number, toast: DeleteToast): Promise<void> {
    this.deleting.update(set => new Set([...set, id]));
    // the files that were to replace this item go with it (the server drops their rows together with the item): they stop now instead of sending up to 300 MB for nothing
    for (const upload of this.uploader.uploads().filter(u => u.replaceId === id)) {
      this.uploader.cancel(upload.uid);
    }
    const work = (async () => {
      try {
        await this.api.remove(id);
        this.invalidateReads();
        this.desired.update(order => (order ? order.filter(x => x !== id) : order));
      } catch (e) {
        this.fail(e, `delete-${toast.kind}`);
      } finally {
        await this.refresh();
        this.deleting.update(set => new Set([...set].filter(x => x !== id)));
      }
    })();
    this.commits.add(work);
    work.finally(() => this.commits.delete(work));
    return work;
  }

  // ------------------------------------------------------------------------------------------------------------------------------ files

  /**
   * The files the editor chose or dropped: each is looked at (first bytes, sides, length), checked against the limits together with the ones before it in the batch,
   * and the good ones go up one after another. `replace` sends the file in place of an item; `front` puts the new item first (the old photo is replaced).
   */
  async pickFiles(files: readonly File[], options: { replace?: { id: number; kind: MediaKind }; front?: boolean } = {}): Promise<void> {
    if (!this.editable() || files.length === 0) {
      return;
    }
    await this.flushDeletes();
    this.messages.update(list => list.filter(m => m.group !== 'batch'));
    const inspected: { file: File; facts: FileFacts; sniffed: Sniffed | null }[] = [];
    for (const file of files) {
      const seen = await inspectFile(file);
      inspected.push({ file, facts: seen.facts, sniffed: seen.sniffed });
    }
    const plan = planBatch(
      inspected.map(i => i.facts),
      { limits: this.limits(), counts: this.counts(), replaceKind: options.replace?.kind }
    );
    if (plan.rejected.length > 0 && plan.accepted.length > 0) {
      this.say('info', null, this.t('checks.partial', { done: plan.accepted.length, total: files.length }), 'batch');
    }
    for (const item of plan.rejected) {
      this.say('error', item.file.name, this.rejectText(item.verdict.reason as RejectReason, item.file, item.verdict.data), 'batch');
    }
    for (const item of plan.accepted) {
      if (item.verdict.lowRes) {
        const d = item.verdict.data;
        this.say('warn', item.file.name, this.t('checks.lowRes', { dims: formatDimensions(d['width'], d['height']), good: d['good'] }), 'batch');
      }
    }
    for (const item of plan.accepted) {
      const source = inspected.find(i => i.facts === item.file);
      if (source?.sniffed) {
        this.upload(source.file, source.sniffed, options);
      }
    }
  }

  /** Starts the file again that stopped on its way: the half-made item is removed, a new one is made. */
  async retryUpload(uid: number): Promise<void> {
    const job = this.uploader.jobOf(uid);
    if (!job || !this.editable()) {
      return;
    }
    const itemId = this.uploader.discard(uid);
    if (itemId !== null) {
      await this.api.remove(itemId).catch(() => undefined);
      this.invalidateReads();
      await this.refresh();
    }
    this.uploader.enqueue({ ...job, hooks: this.hooksFor(job.file.name, job.replaceId !== null, false) });
  }

  /** The editor stops a file that is going up: the item it had made is removed at once (nothing was published, there is nothing to take back). */
  async cancelUpload(uid: number): Promise<void> {
    const itemId = this.uploader.cancel(uid);
    if (itemId !== null) {
      await this.api.remove(itemId).catch(() => undefined);
      this.invalidateReads();
      await this.refresh();
    }
  }

  /** Drops the new file that was to replace an item: the old item goes on as it was. */
  async cancelReplacement(target: MediaItem): Promise<void> {
    const local = this.uploader.uploads().find(u => u.replaceId === target.id);
    if (local) {
      await this.cancelUpload(local.uid);
      return;
    }
    if (target.replacement) {
      await this.run(target.id, async () => {
        await this.api.remove((target.replacement as MediaItem).id);
        this.invalidateReads();
        await this.refresh();
      });
    }
  }

  /** One file no longer waits for the network; with none left, the notice «Пропал интернет…» goes. */
  private networkBack(key: number): void {
    if (this.offline.delete(key) && this.offline.size === 0) {
      this.messages.update(list => list.filter(m => m.group !== 'offline'));
    }
  }

  private upload(file: File, sniffed: Sniffed, options: { replace?: { id: number; kind: MediaKind }; front?: boolean }): void {
    this.uploader.enqueue({
      file,
      sniffed,
      productInfoId: this.productInfoId,
      replaceId: options.replace?.id ?? null,
      hooks: this.hooksFor(file.name, !!options.replace, !!options.front)
    });
  }

  private hooksFor(fileName: string, replacing: boolean, front: boolean): UploadHooks {
    const key = ++this.hookSeq;
    return {
      started: async itemId => {
        await this.refresh();
        if (front && !replacing) {
          const rest = this.fullOrder().filter(id => id !== itemId);
          if (rest.length > 0 && this.fullOrder().includes(itemId)) {
            this.desired.set([itemId, ...rest]);
            await this.syncOrder();
          }
        }
      },
      finished: (item, preview) => {
        if (preview) {
          this.localThumbs.update(map => new Map(map).set(item.id, preview));
        }
        this.savedNote.set(replacing ? 'replaced' : 'uploaded');
        void this.refresh();
      },
      refused: e => {
        this.fail(e, 'upload', fileName);
      },
      reconnecting: () => {
        this.offline.add(key);
        this.say('info', null, this.t('checks.offline'), 'offline');
      },
      // the notice stays while any file still waits for the network, and goes when the last one has its answer, is done, stopped or cancelled
      restored: () => this.networkBack(key),
      ended: () => this.networkBack(key),
      failed: e => {
        if (e instanceof UploadUrlRejectedError) {
          // the server named a place that is not the cloud: nothing was sent; the editor is told plainly, on the tile and in a line under the grid
          const text = this.t('errors.uploadAddress');
          this.say('error', fileName, text, 'upload');
          void this.refresh();
          return text;
        }
        const error = mediaErrorOf(e);
        void this.refresh();
        return error ? mediaErrorText(this.text, error.message, error.data, this.lang()) : this.t('tile.uploadFailed');
      }
    };
  }

  // ------------------------------------------------------------------------------------------------------------------------------ item actions

  /** FAILED → PROCESSING again. */
  async retry(id: number): Promise<void> {
    await this.run(id, async () => {
      await this.api.retry(id);
      this.invalidateReads();
      this.savedNote.set('retry');
      await this.refresh();
    });
  }

  /** A new frame (and cover) for an item. Throws the error to the dialog that asked, which shows it; the list is read again either way. */
  async saveFrame(id: number, crop: MediaCrop, posterMs: number | undefined): Promise<void> {
    this.busy.update(set => new Set([...set, id]));
    try {
      await this.api.setFrame(id, { crop, ...(posterMs !== undefined ? { posterMs } : {}) });
      this.invalidateReads();
      this.savedNote.set('frame');
    } catch (e) {
      if (isForbidden(e)) {
        this.forbidden.set(true);
      }
      throw e;
    } finally {
      this.busy.update(set => new Set([...set].filter(x => x !== id)));
      await this.refresh();
    }
  }

  isBusy(id: number | null): boolean {
    return id !== null && this.busy().has(id);
  }

  /** Runs a request for one item with its buttons out of reach, then shows the error if there is one. */
  private async run(id: number, work: () => Promise<void>): Promise<void> {
    if (this.busy().has(id)) {
      return;
    }
    this.busy.update(set => new Set([...set, id]));
    try {
      await work();
    } catch (e) {
      this.fail(e, 'action');
      await this.refresh();
    } finally {
      this.busy.update(set => new Set([...set].filter(x => x !== id)));
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------ copy from another size

  /** The other sizes of the cake that have photos or video, with their numbers (one call, made when the list is first opened). */
  async loadSiblings(): Promise<void> {
    if (this.siblings() !== null || this.siblingsLoading()) {
      return;
    }
    this.siblingsLoading.set(true);
    this.siblingsFailed.set(false);
    try {
      const answer = await this.api.siblings(this.productInfoId);
      const lang = this.lang();
      const withMedia = answer.sizes.filter(s => s.photos + s.videos > 0);
      const result: Sibling[] = await Promise.all(
        withMedia.map(async s => ({
          id: s.productInfoId,
          title: uiLabel(lang, s.title) ?? uiLabel('ru', s.title) ?? `#${s.productInfoId}`,
          photos: s.photos,
          videos: s.videos,
          ready: s.ready,
          thumb: s.ready > 0 ? await this.thumbOfSize(s.productInfoId) : null
        }))
      );
      this.siblings.set(result);
    } catch {
      this.siblingsFailed.set(true);
    } finally {
      this.siblingsLoading.set(false);
    }
  }

  /** Takes the photos and video of another size instead of this size's own. */
  async copyFrom(sibling: Sibling): Promise<MediaCopy | null> {
    if (!this.editable() || this.copyBusy() || this.uploader.active) {
      return null;
    }
    this.copyBusy.set(true);
    try {
      await this.flushDeletes();
      const result = await this.api.copy({ fromProductInfoId: sibling.id, toProductInfoId: this.productInfoId });
      this.invalidateReads();
      this.desired.set(null);
      this.savedNote.set('copied');
      this.say('info', null, this.t(result.skipped > 0 ? 'copy.doneSkipped' : 'copy.done', { copied: result.copied, skipped: result.skipped }), 'copy');
      await this.refresh();
      return result;
    } catch (e) {
      this.fail(e, 'copy');
      await this.refresh();
      return null;
    } finally {
      this.copyBusy.set(false);
    }
  }

  private async thumbOfSize(id: number): Promise<{ src: string; srcset: string | null } | null> {
    try {
      const list = await this.api.list(id);
      for (const item of list.items) {
        if (item.src && item.widths?.length) {
          return publicThumb(item.src, item.widths, 34);
        }
      }
    } catch {
      // a picture is a nicety: the line is there without it
    }
    return null;
  }

  // ------------------------------------------------------------------------------------------------------------------------------ messages

  dismiss(id: number): void {
    this.messages.update(list => list.filter(m => m.id !== id));
  }

  dismissAll(): void {
    this.messages.set([]);
  }

  say(level: MediaMessage['level'], file: string | null, text: string, group: string): void {
    this.messages.update(list => [...list.filter(m => group === 'batch' || m.group !== group), { id: ++this.messageSeq, level, file, text, group }]);
  }

  /** A failed request becomes a line the editor can read; a refusal of the rights turns the controls off. */
  private fail(err: unknown, group: string, file: string | null = null): void {
    if (isForbidden(err)) {
      this.forbidden.set(true);
      this.say('error', file, this.t('errors.forbidden'), group);
      return;
    }
    const error = mediaErrorOf(err);
    this.say('error', file, error ? mediaErrorText(this.text, error.message, error.data, this.lang()) : this.t('errors.failed'), group);
  }

  private rejectText(reason: RejectReason, file: FileFacts, data: Record<string, number>): string {
    const kind = file.sniffed?.type ?? 'PHOTO';
    const lang = this.lang();
    switch (reason) {
      case 'tooLarge':
        return this.t(`checks.tooLarge.${kind}`, { size: formatSize(data['size'], lang), max: formatSize(data['max'], lang) });
      case 'tooSmall':
        return this.t('checks.tooSmall', { dims: formatDimensions(data['width'], data['height']), min: data['min'], good: data['good'] });
      case 'tooLong':
        return this.t('checks.tooLong', { duration: formatClock(data['seconds']), max: data['max'] });
      case 'videoTooSmall':
        return this.t('checks.videoTooSmall', { dims: formatDimensions(data['width'], data['height']), min: data['min'] });
      default:
        return this.t(`checks.${reason}`, data);
    }
  }

  // ------------------------------------------------------------------------------------------------------------------------------ polling and the end

  /**
   * An answer to a change (order, frame, copy, deletion, retry…) is the newest word about the size. A read that is on its way was asked before it and may carry the
   * state from before the change: bumping the number makes `refresh` drop it.
   */
  private invalidateReads(): void {
    this.requestSeq++;
  }

  private apply(list: MediaList): void {
    const legacy = list.legacyPhoto?.url ?? null;
    const before = this.list();
    if (legacy !== null && legacy !== (before?.legacyPhoto?.url ?? null)) {
      this.legacyStamp.set(Date.now());
      if (before !== null) {
        // it appeared while the page was open (the last photo was deleted): the server is about to put the file back
        this.restampLegacyLater();
      }
    }
    this.list.set(list);
    this.settleDesired();
    this.dropFinishedThumbs(list.items);
    this.schedulePoll();
  }

  /** The order the editor put is forgotten once the server has it (and nothing waits for a deletion). */
  private settleDesired(): void {
    const list = this.list();
    const desired = this.desired();
    if (!list || !desired || this.orderBusy() || this.hidden().size > 0) {
      return;
    }
    const server = serverOrder(list.items);
    if (sameOrder(mergeOrder(desired, server), server)) {
      this.desired.set(null);
    }
  }

  /** The picture of a file that went up is given back once the server has a picture of its own for the item. */
  private dropFinishedThumbs(items: readonly MediaItem[]): void {
    const kept = this.localThumbs();
    if (kept.size === 0) {
      return;
    }
    const next = new Map(kept);
    for (const item of items) {
      const url = next.get(item.id);
      if (url && (item.src || item.previewUrl)) {
        this.uploader.releasePreview(url);
        next.delete(item.id);
      }
    }
    for (const id of [...next.keys()]) {
      if (!items.some(i => i.id === id)) {
        const url = next.get(id) as string;
        this.uploader.releasePreview(url);
        next.delete(id);
      }
    }
    if (next.size !== kept.size) {
      this.localThumbs.set(next);
    }
  }

  private restampLegacyLater(): void {
    this.restampTimers.forEach(timer => clearTimeout(timer));
    this.restampTimers = LEGACY_RESTAMP_MS.map(ms =>
      setTimeout(() => {
        if (!this.destroyed && this.list()?.legacyPhoto) {
          this.legacyStamp.set(Date.now());
        }
      }, ms)
    );
  }

  /** Is anything still being made (a file going up, an item being processed)? */
  needsPolling(): boolean {
    const list = this.list();
    if (!list) {
      return false;
    }
    return list.items.some(i => i.status === 'UPLOADING' || i.status === 'PROCESSING' || i.replacement?.status === 'UPLOADING' || i.replacement?.status === 'PROCESSING');
  }

  private schedulePoll(): void {
    if (this.pollTimer !== null || this.destroyed || !this.needsPolling()) {
      return;
    }
    const processing = this.list()?.items.some(i => i.status === 'PROCESSING' || i.replacement?.status === 'PROCESSING') ?? false;
    this.pollTimer = setTimeout(
      () => {
        this.pollTimer = null;
        void this.refresh();
      },
      processing || this.uploader.active ? POLL_FAST_MS : POLL_SLOW_MS
    );
  }

  /** The page is left: the deletions that wait are sent, the files stop, the memory goes back. */
  destroy(): void {
    this.destroyed = true;
    if (this.pollTimer !== null) {
      clearTimeout(this.pollTimer);
      this.pollTimer = null;
    }
    this.restampTimers.forEach(timer => clearTimeout(timer));
    this.restampTimers = [];
    this.queue.flush();
    this.queue.dispose();
    this.uploader.destroy();
    for (const url of this.localThumbs().values()) {
      URL.revokeObjectURL(url);
    }
    this.localThumbs.set(new Map());
    this.previews.release();
  }

  // ------------------------------------------------------------------------------------------------------------------------------ texts

  private lang(): string {
    return pageLang(this.translate.getActiveLang());
  }

  private readonly text = (key: string, params?: Record<string, unknown>): string => this.translate.translate(key, params);

  /** A text of the block `media` of the texts of the page. */
  t(key: string, params?: Record<string, unknown>): string {
    return this.translate.translate(`${MEDIA_TEXTS}.${key}`, params);
  }
}
